import React, { useState } from 'react';
import { X, Check, Loader2, Zap } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';

interface PricingModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleCheckout = async (priceId: string) => {
        if (!user) return;
        setLoadingPriceId(priceId);

        try {
            const { data, error } = await supabase.functions.invoke('create-checkout-session', {
                body: { price_id: priceId }
            });

            if (error) throw error;

            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (error: any) {
            console.error("Erro no checkout:", error);
            alert("Falha ao iniciar pagamento. Verifique as configurações das Edge Functions.");
        } finally {
            setLoadingPriceId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-[32px] w-full max-w-4xl shadow-2xl relative overflow-hidden flex flex-col md:flex-row">

                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 bg-slate-100 rounded-full text-slate-500 hover:bg-slate-200 transition-colors z-20"
                >
                    <X size={20} />
                </button>

                {/* Informações */}
                <div className="p-8 md:p-12 md:w-1/2 flex flex-col justify-center bg-slate-50 relative z-10">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6">
                        <Zap size={24} />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">Desbloqueie todo o seu potencial</h2>
                    <p className="text-slate-500 mb-8">
                        Faça upgrade para o Plano Pro e crie projetos ilimitados, retenha seu histórico para sempre e tenha suporte prioritário.
                    </p>

                    <div className="space-y-4">
                        {[
                            'Projetos ilimitados por mês',
                            'Favoritos ilimitados',
                            'Histórico mantido permanentemente',
                            'Exportação avançada de PDF',
                            'Suporte prioritário'
                        ].map((feature, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                                    <Check size={14} strokeWidth={3} />
                                </div>
                                <span className="text-slate-700 font-medium">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pricing Options */}
                <div className="p-8 md:p-12 md:w-1/2 bg-white flex flex-col gap-6 justify-center relative z-10">

                    {/* Mensal */}
                    <div className="border-2 border-slate-200 hover:border-blue-500 rounded-2xl p-6 transition-colors cursor-pointer group"
                        onClick={() => !loadingPriceId && handleCheckout('price_1T828kC9SA3osKMQ7QZshCVg')}>
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-xl font-bold text-slate-900">Mensal</h3>
                        </div>
                        <div className="flex items-baseline gap-1 mb-4">
                            <span className="text-3xl font-bold text-slate-900">R$ 29</span>
                            <span className="text-slate-500 font-medium">/mês</span>
                        </div>
                        <button
                            disabled={!!loadingPriceId}
                            className="w-full py-3 rounded-xl bg-slate-100 text-slate-900 font-bold group-hover:bg-blue-600 group-hover:text-white transition-colors flex justify-center items-center gap-2"
                        >
                            {loadingPriceId === 'price_1T828kC9SA3osKMQ7QZshCVg' ? <Loader2 className="animate-spin" size={20} /> : 'Assinar Mensal'}
                        </button>
                    </div>

                    {/* Anual */}
                    <div className="border-2 border-blue-500 rounded-2xl p-6 relative shadow-lg shadow-blue-500/10 cursor-pointer group transition-transform hover:-translate-y-1"
                        onClick={() => !loadingPriceId && handleCheckout('price_1T82BVC9SA3osKMQvtRJIiuY')}>
                        <div className="absolute top-0 right-6 -translate-y-1/2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                            Desconto de 22%
                        </div>
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-xl font-bold text-slate-900">Anual</h3>
                        </div>
                        <div className="flex flex-col mb-4">
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl font-bold text-slate-900">R$ 23,17</span>
                                <span className="text-slate-500 font-medium">/mês</span>
                            </div>
                            <span className="text-sm text-slate-500 mt-1">Faturado R$ 278 anualmente</span>
                        </div>
                        <button
                            disabled={!!loadingPriceId}
                            className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors flex justify-center items-center gap-2 shadow-md shadow-blue-500/20"
                        >
                            {loadingPriceId === 'price_1T82BVC9SA3osKMQvtRJIiuY' ? <Loader2 className="animate-spin" size={20} /> : 'Assinar Anual'}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};
